import { useMemo, type ElementType } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Brain, Trophy } from "lucide-react";
import { Badge } from "../../components/ui/Badge";
import { EmptyStateCard } from "../../components/ui/EmptyStateCard";
import { ListRow } from "../../components/ui/ListRow";
import { Skeleton } from "../../components/ui/Skeleton";
import { useStudySubjects } from "../../lib/useStudySubjects";

/** Shared course picker: choose a course (grouped by semester), then continue inside that course. */
function CourseHub({ id, title, subtitle, tab, Icon, emptyTitle }: { id: string; title: string; subtitle: string; tab: "quiz" | "exams"; Icon: ElementType; emptyTitle: string }) {
  const { subjects, loading } = useStudySubjects();
  const navigate = useNavigate();
  const bySemester = useMemo(() => {
    const map = new Map<number, typeof subjects>();
    for (const s of subjects) map.set(s.semester ?? 1, [...(map.get(s.semester ?? 1) ?? []), s]);
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [subjects]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="space-y-1">
        <h1 className="text-section font-bold font-display">{title}</h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : bySemester.length === 0 ? (
        <EmptyStateCard icon={Icon} title={emptyTitle} message="Courses for your stream will appear here." action={{ label: "Open Study Hub", onClick: () => navigate("/student/study") }} />
      ) : (
        bySemester.map(([semester, list]) => (
          <section key={semester} className="space-y-2" aria-labelledby={`${id}-sem-${semester}`}>
            <h2 id={`${id}-sem-${semester}`} className="text-lg font-bold font-display">Semester {semester}</h2>
            <ul className="space-y-2">
              {list.map((s) => (
                <li key={s.id}>
                  <ListRow
                    to={`/student/study/${s.slug}?tab=${tab}`}
                    icon={<Icon size={18} />}
                    title={s.name}
                    subtitle={<span className="flex items-center gap-2"><span className="font-mono">{s.code}</span>{(s as { isPremium?: boolean }).isPremium && <Badge variant="warning">Premium</Badge>}</span>}
                    action={<ArrowUpRight size={18} className="text-muted-foreground" aria-hidden="true" />}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

/** Quizzes from anywhere outside a course (dashboard, study hub, menu): choose a course, then a chapter, then the quiz. */
export function QuizHubPage() {
  return <CourseHub id="quiz" title="Quizzes" subtitle="Choose a course to see the chapters that have quizzes." tab="quiz" Icon={Brain} emptyTitle="No courses yet" />;
}

/** Past exams: same pattern as quizzes. Choose a course to see its past exams. */
export function ExamHubPage() {
  return <CourseHub id="exam" title="Past exams" subtitle="Choose a course to see its past exams." tab="exams" Icon={Trophy} emptyTitle="No courses yet" />;
}
